import { Injectable, NgZone } from '@angular/core';
import { Canvas, Point } from 'fabric';
import { MnemonicGridRenderer } from './mnemonic-grid.renderer';

export interface MnemonicCanvasEventHandlers {
    onMouseDown?(event: any): void;
    onMouseMove?(event: any): void;
    onMouseUp?(): void;
    onDrop?(event: any): void;
    onSelectionCreated?(event: any): void;
    onSelectionUpdated?(event: any): void;
    onObjectAdded?(event: any): void;
    onObjectModified?(event: any): void;
    onBeforeTransform?(event: any): void;
    onObjectMoving?(event: any): void;
    onDoubleClick?(target: any): void;
    onMouseOver?(event: any): void;
    onMouseOut?(target: any): void;
    onUndo?(): void | Promise<void>;
    onRedo?(): void | Promise<void>;
    onDelete?(): void | Promise<void>;
    canUndo?(): boolean;
    canRedo?(): boolean;
    canDelete?(): boolean;
}

/**
 * Observer — подписка на события Fabric canvas с единой точкой регистрации/отписки.
 */
@Injectable({
    providedIn: 'root',
})
export class MnemonicCanvasEventsService {
    private gridHandler: (() => void) | null = null;
    private keydownHandler: ((event: KeyboardEvent) => void) | null = null;

    constructor(private ngZone: NgZone) {}

    bind(canvas: Canvas, handlers: MnemonicCanvasEventHandlers): void {
        this.unbind(canvas);

        this.gridHandler = MnemonicGridRenderer.createAfterRenderHandler(() => canvas);
        canvas.on('after:render', this.gridHandler);

        if (handlers.onMouseDown) {
            canvas.on('mouse:down', (event) => this.ngZone.run(() => handlers.onMouseDown!(event)));
        }

        if (handlers.onMouseMove) {
            canvas.on('mouse:move', (event) => this.ngZone.run(() => handlers.onMouseMove!(event)));
        }

        if (handlers.onMouseUp) {
            canvas.on('mouse:up', () => this.ngZone.run(() => handlers.onMouseUp!()));
        }

        if (handlers.onDrop) {
            canvas.on('drop', (event) => this.ngZone.run(() => handlers.onDrop!(event)));
        }

        if (handlers.onSelectionCreated) {
            canvas.on('selection:created', (event) => this.ngZone.run(() => handlers.onSelectionCreated!(event)));
        }

        if (handlers.onSelectionUpdated) {
            canvas.on('selection:updated', (event) => this.ngZone.run(() => handlers.onSelectionUpdated!(event)));
        }

        if (handlers.onObjectAdded) {
            canvas.on('object:added', (event) => this.ngZone.run(() => handlers.onObjectAdded!(event)));
        }

        if (handlers.onObjectModified) {
            canvas.on('object:modified', (event) => this.ngZone.run(() => handlers.onObjectModified!(event)));
        }

        if (handlers.onBeforeTransform) {
            canvas.on('before:transform', (event) => this.ngZone.run(() => handlers.onBeforeTransform!(event)));
        }

        if (handlers.onObjectMoving) {
            canvas.on('object:moving', (event) => this.ngZone.run(() => handlers.onObjectMoving!(event)));
        }

        if (handlers.onDoubleClick) {
            canvas.on('mouse:dblclick', (event) => this.ngZone.run(() => handlers.onDoubleClick!(event.target)));
        }

        if (handlers.onMouseOver) {
            canvas.on('mouse:over', (event) => this.ngZone.run(() => handlers.onMouseOver!(event)));
        }

        if (handlers.onMouseOut) {
            canvas.on('mouse:out', (event) => this.ngZone.run(() => handlers.onMouseOut!(event.target)));
        }

        canvas.on('mouse:wheel', (event) => {
            if (!event.e.ctrlKey) {
                return;
            }

            const delta = event.e.deltaY;
            let zoom = canvas.getZoom();
            zoom *= 0.999 ** delta;
            zoom = Math.min(20, Math.max(0.01, zoom));

            canvas.zoomToPoint(new Point({ x: event.e.offsetX, y: event.e.offsetY }), zoom);
            event.e.preventDefault();
            event.e.stopPropagation();
        });

        this.keydownHandler = async (event: KeyboardEvent) => {
            const targetTag = (event.target as HTMLElement | null)?.tagName;

            if (targetTag === 'INPUT' || targetTag === 'TEXTAREA' || (event.target as HTMLElement | null)?.isContentEditable) {
                return;
            }

            if ((event.key === 'Delete' || event.key === 'Backspace') && handlers.canDelete?.()) {
                event.preventDefault();
                await handlers.onDelete?.();
                return;
            }

            if (event.ctrlKey && event.key === 'z' && handlers.canUndo?.()) {
                await handlers.onUndo?.();
            }

            if (event.ctrlKey && event.key === 'y' && handlers.canRedo?.()) {
                await handlers.onRedo?.();
            }
        };

        canvas.upperCanvasEl?.addEventListener('contextmenu', (event) => event.preventDefault());

        document.addEventListener('keydown', this.keydownHandler);
    }

    unbind(canvas: Canvas | null | undefined): void {
        if (this.gridHandler && canvas) {
            canvas.off('after:render', this.gridHandler);
        }

        this.gridHandler = null;

        if (this.keydownHandler) {
            document.removeEventListener('keydown', this.keydownHandler);
            this.keydownHandler = null;
        }
    }
}
